import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import axios from "axios";
import styled from "styled-components";

const FormContainer = styled.div`
  max-width: 600px;
  margin: 50px auto;
  padding: 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const FormTitle = styled.h2`
  margin: 0 0 30px 0;
  font-size: 28px;
  font-weight: 600;
  color: #333;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #555;
`;

const StyledInput = styled(Field)`
  width: 100%;
  padding: 12px 16px;
  font-size: 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #ff4d71;
    box-shadow: 0 0 0 3px rgba(255, 77, 113, 0.1);
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  font-size: 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #ff4d71;
    box-shadow: 0 0 0 3px rgba(255, 77, 113, 0.1);
  }
`;

const FileInputWrapper = styled.div`
  position: relative;
`;

const FileInputLabel = styled.label`
  display: block;
  padding: 12px 16px;
  font-size: 16px;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  text-align: center;
  color: #666;

  &:hover {
    border-color: #ff4d71;
    background-color: #fff5f7;
  }
`;

const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

const FileNameDisplay = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: #666;
  font-style: italic;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: #fafafa;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #555;
  cursor: pointer;
  margin: 0;
`;

const StyledCheckbox = styled(Field)`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #ff4d71;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background-color: #ff4d71;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s, box-shadow 0.2s;
  margin-top: 8px;

  &:hover:not(:disabled) {
    background-color: #ff3366;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 77, 113, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const MessageContainer = styled.div`
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: slideDown 0.3s ease-out;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SuccessMessage = styled(MessageContainer)`
  background-color: #d4edda;
  border: 2px solid #c3e6cb;
  color: #155724;
`;

const ErrorMessage = styled(MessageContainer)`
  background-color: #f8d7da;
  border: 2px solid #f5c6cb;
  color: #721c24;
`;

const MessageIcon = styled.span`
  font-size: 20px;
  font-weight: bold;
`;

const MessageText = styled.span`
  flex: 1;
  font-size: 15px;
  font-weight: 500;
`;

const BookForm = () => {
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  return (
    <FormContainer>
      <FormTitle>Create New Book</FormTitle>
      <Formik
        initialValues={{ name: "", file: null, colour: true, allowAlternatives: false }}
        onSubmit={(
          { file, name, colour, allowAlternatives },
          { setSubmitting, resetForm },
        ) => {
          // Clear previous messages
          setSuccessMessage(null);
          setErrorMessage(null);

          let formData = new FormData();
          formData.append("file", file);
          formData.append("name", name);
          formData.append("colour", colour);
          formData.append("allowAlternatives", allowAlternatives);
          axios
            .post(`/api/book`, formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            })
            .then(() => {
              setSubmitting(false);
              setSuccessMessage("Book created successfully!");
              resetForm();
              // Clear success message after 5 seconds
              setTimeout(() => {
                setSuccessMessage(null);
              }, 5000);
            })
            .catch((error) => {
              setSubmitting(false);
              const errorMsg = error.response?.data?.error || error.message || "Failed to create book. Please try again.";
              setErrorMessage(errorMsg);
            });
        }}
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <Form>
            {successMessage && (
              <SuccessMessage>
                <MessageIcon>✓</MessageIcon>
                <MessageText>{successMessage}</MessageText>
              </SuccessMessage>
            )}
            {errorMessage && (
              <ErrorMessage>
                <MessageIcon>✕</MessageIcon>
                <MessageText>{errorMessage}</MessageText>
              </ErrorMessage>
            )}

            <FormGroup>
              <Label htmlFor="name">Book Name</Label>
              <StyledInput
                type="text"
                id="name"
                name="name"
                placeholder="Enter book name"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="file">PGN File</Label>
              <FileInputWrapper>
                <FileInputLabel htmlFor="file">
                  {values.file ? values.file.name : "Choose a file or drag it here"}
                </FileInputLabel>
                <HiddenFileInput
                  id="file"
                  name="file"
                  type="file"
                  accept=".pgn"
                  onChange={(event) => {
                    setFieldValue("file", event.currentTarget.files[0]);
                  }}
                />
              </FileInputWrapper>
              {values.file && (
                <FileNameDisplay>Selected: {values.file.name}</FileNameDisplay>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="colour">Color</Label>
              <Field as={StyledSelect} id="colour" name="colour">
                <option value={true}>White</option>
                <option value={false}>Black</option>
              </Field>
            </FormGroup>

            <FormGroup>
              <CheckboxGroup>
                <CheckboxLabel htmlFor="allowAlternatives">
                  <StyledCheckbox
                    type="checkbox"
                    id="allowAlternatives"
                    name="allowAlternatives"
                  />
                  Allow Alternatives
                </CheckboxLabel>
              </CheckboxGroup>
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Create Book"}
            </SubmitButton>
          </Form>
        )}
      </Formik>
    </FormContainer>
  );
};

export default BookForm;
